import { useState, useEffect, useRef, forwardRef } from 'react'
import { Link } from 'react-router-dom'
import {
  MapPin, Clock, Calendar, Users, Award, ArrowRight,
  Linkedin, Instagram, Twitter, Facebook, Play, Mic,
  BookOpen, Video, Image, Mic2, FileText, BadgeCheck, Download,
} from 'lucide-react'
import CinematicNav, { BackToTop } from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import Reveal from '../components/landing/Reveal'
import Breadcrumbs from '../components/masterclass/Breadcrumbs'
import MasterclassCountdown from '../components/masterclass/MasterclassCountdown'
import MasterclassRegistrationForm from '../components/masterclass/MasterclassRegistrationForm'
import DigitalEventPass from '../components/masterclass/DigitalEventPass'
import SpeakerImage from '../components/masterclass/SpeakerImage'
import { resolveImageUrl } from '../utils/imageUrl'
import { formatScheduleFallback, isValidEventDate, SCHEDULE_SERIES_NAME } from '../utils/masterclassSchedule'
import { masterclassService } from '../services/masterclassService'

const QUICK_NAV = [
  { icon: Calendar, title: 'Upcoming Session', desc: 'Register for this month', to: '/masterclass', cta: true },
  { icon: BookOpen, title: 'Archive', desc: 'Browse past sessions', to: '/masterclass/archive' },
  { icon: Video, title: 'Videos', desc: 'Watch recordings & highlights', to: '/masterclass/videos' },
  { icon: Image, title: 'Gallery', desc: 'Photos from every event', to: '/masterclass/gallery' },
  { icon: Mic2, title: 'Speakers', desc: 'Meet our guest speakers', to: '/masterclass/speakers' },
  { icon: FileText, title: 'Resources', desc: 'Slides, downloads & notes', to: '/masterclass/resources' },
  { icon: BadgeCheck, title: 'Certificates', desc: 'Verify or download', to: '/verify-certificate' },
]

const DEFAULT_LEARN = [
  { icon: '🎯', text: 'Creative mindset' },
  { icon: '💪', text: 'Building confidence' },
  { icon: '🤝', text: 'Networking skills' },
  { icon: '📈', text: 'Career growth' },
  { icon: '🏭', text: 'Industry expectations' },
  { icon: '🎤', text: 'Live Q&A session' },
  { icon: '💡', text: 'Practical advice' },
  { icon: '📂', text: 'Portfolio tips' },
]

function Confetti() {
  const colors = ['#F7941D', '#ffffff', '#22c55e', '#3b82f6', '#f59e0b', '#ec4899']
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
      {Array.from({ length: 60 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 rounded-sm"
          style={{
            background: colors[i % colors.length],
            left: `${Math.random() * 100}%`,
            top: `-10px`,
            animation: `confetti-fall ${1.5 + Math.random() * 2}s ${Math.random() * 1.5}s linear forwards`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  )
}

function SkeletonHero() {
  return (
    <div className="cinema-page min-h-screen flex items-center justify-center">
      <div className="cinema-container grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6 animate-pulse">
          <div className="h-3 w-32 bg-white/8 rounded-sm" />
          <div className="h-20 w-3/4 bg-white/8 rounded-sm" />
          <div className="h-20 w-1/2 bg-white/8 rounded-sm" />
          <div className="h-3 w-full bg-white/5 rounded-sm" />
          <div className="flex gap-4">
            <div className="h-14 w-40 bg-white/8 rounded-sm" />
            <div className="h-14 w-40 bg-white/5 rounded-sm" />
          </div>
        </div>
        <div className="h-[520px] bg-white/5 rounded-sm animate-pulse" />
      </div>
    </div>
  )
}

export default function MasterclassPage() {
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [masterclass, setMasterclass] = useState(null)
  const [pastSessions, setPastSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [registered, setRegistered] = useState(null)
  const [showConfetti, setShowConfetti] = useState(false)
  const [downloadingFlyer, setDownloadingFlyer] = useState(false)
  const formRef = useRef(null)

  useEffect(() => {
    document.title = `${SCHEDULE_SERIES_NAME} – The Creative Talk Series | Synergy Multimedia Academy`
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    Promise.all([
      masterclassService.getUpcoming().catch(() => ({ masterclass: null })),
      masterclassService.getPast().catch(() => ({ sessions: [] })),
    ]).then(([upcoming, past]) => {
      setMasterclass(upcoming.masterclass)
      setPastSessions(past.sessions || [])
    }).finally(() => setLoading(false))
  }, [])

  const handleSuccess = (reg) => {
    setRegistered(reg)
    setShowConfetti(true)
    setTimeout(() => setShowConfetti(false), 4000)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const handleDownloadFlyer = async () => {
    if (!mc) return
    const id = mc.slug || mc.id
    setDownloadingFlyer(true)
    try {
      await masterclassService.publicDownloadFlyer(id)
    } catch (err) {
      console.error('Flyer download failed:', err)
    } finally {
      setDownloadingFlyer(false)
    }
  }

  const mc = masterclass
  const speaker = mc ? {
    name: mc.speaker_name,
    title: mc.speaker_title,
    company: mc.speaker_company,
    bio: mc.speaker_bio,
    experience: mc.speaker_experience,
    photo: mc.speaker_photo,
    linkedin: mc.linkedin_url,
    instagram: mc.instagram_url,
    twitter: mc.twitter_url,
    facebook: mc.facebook_url,
  } : null

  const whatYouLearn = (() => {
    if (!mc) return DEFAULT_LEARN
    try {
      const raw = mc.what_you_learn
      if (Array.isArray(raw) && raw.length) return raw
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_LEARN
      }
      return DEFAULT_LEARN
    } catch { return DEFAULT_LEARN }
  })()

  const isValidDate = isValidEventDate
  const formatDate = (d) => {
    if (!isValidDate(d)) return formatScheduleFallback()
    const raw = String(d)
    const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/)
    const date = dateOnly ? new Date(`${dateOnly[1]}T12:00:00+03:00`) : new Date(d)
    return date.toLocaleDateString('en-UG', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Africa/Kampala',
    })
  }
  const formatTime = (t) => t || '5:00 PM'

  if (loading) return <SkeletonHero />

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <div className="cinema-grain" aria-hidden="true" />
      {showConfetti && <Confetti />}

      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      {/* ── CONFIRMATION VIEW ───────────────────────────────────────────────── */}
      {registered ? (
        <section className="relative min-h-screen flex items-center justify-center py-32 overflow-hidden">
          <div className="cinema-glow-orb w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-40" />
          <div className="cinema-container max-w-2xl w-full text-center relative">
            <Reveal>
              <div className="text-6xl mb-8">🎉</div>
              <span className="cinema-label">Registration Confirmed</span>
              <h1 className="cinema-headline-xl mt-6">
                You're <span className="text-cinema-gold">Registered!</span>
              </h1>
              <p className="cinema-body-lg mt-8 max-w-md mx-auto">
                Thank you, <strong className="text-white">{registered.first_name}</strong>. Your digital pass has been generated.
              </p>
              <p className="text-cinema-muted font-sans text-sm mt-3 mb-12">See you on the last Friday of the month. 🚀</p>
            </Reveal>
            <Reveal delay={200}>
              <DigitalEventPass registration={registered} />
            </Reveal>
            <button onClick={() => setRegistered(null)}
              className="mt-10 cinema-btn-text">
              ← Back to Masterclass
            </button>
          </div>
        </section>
      ) : (
        <>
          {/* ── HERO ──────────────────────────────────────────────────────── */}
          <section className="relative min-h-[100svh] flex items-end overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-cinema-black/60 via-cinema-black/40 to-cinema-black" />
            <div className="cinema-glow-orb w-[700px] h-[700px] -top-40 -right-40 opacity-60" />
            <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-20 -left-20" />

            <div className="relative z-10 w-full pt-24 sm:pt-32 pb-12 sm:pb-24">
              <div className="cinema-container">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-end">

                  {/* Left */}
                  <div className="max-w-2xl">
                    <Breadcrumbs />
                    <span className="cinema-label mt-8 mb-6 block">Kampala · Uganda · Last Friday Monthly</span>

                    <h1>
                      <span className="block cinema-headline-xl font-extralight text-white/90">The SMA</span>
                      <span className="block cinema-headline-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-cinema-gold via-cinema-glow to-cinema-amber">Monthly</span>
                      <span className="block cinema-headline-xl font-extralight text-white/90">Masterclass</span>
                    </h1>

                    <p className="mt-6 font-sans text-xs uppercase tracking-[0.5em] text-cinema-muted">
                      The Creative Talk Series
                    </p>
                    <div className="mt-4 h-px w-20 bg-gradient-to-r from-cinema-gold to-transparent" />

                    <p className="mt-8 cinema-body max-w-lg italic text-cinema-soft/60">
                      Where Industry Meets Education.
                    </p>
                    <p className="mt-3 cinema-body text-sm max-w-lg">
                      On the last Friday of each month we bring Uganda's leading creatives, entrepreneurs and innovators to mentor the next generation.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                      <button onClick={scrollToForm} className="cinema-btn-primary group">
                        Register Now
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </button>
                      {mc && (
                        <button
                          onClick={handleDownloadFlyer}
                          disabled={downloadingFlyer}
                          className="cinema-btn-ghost group disabled:opacity-50"
                        >
                          <Download className="w-4 h-4" />
                          {downloadingFlyer ? 'Generating…' : 'Download Flyer'}
                        </button>
                      )}
                      {pastSessions.length > 0 && (
                        <a href="#past-sessions" className="cinema-btn-ghost group">
                          <Play className="w-4 h-4" />
                          Past Masterclasses
                        </a>
                      )}
                    </div>

                    <div className="mt-10 max-w-sm">
                      <MasterclassCountdown eventDate={mc?.event_date} />
                    </div>
                  </div>

                  {/* Right – speaker card */}
                  <div className="relative mt-12 lg:mt-0">
                    {/* THIS WEEK badge */}
                    <div className="absolute -top-4 -left-3 lg:-top-6 lg:-left-6 z-10 bg-cinema-glow px-4 lg:px-5 py-3 lg:py-4 text-center">
                      <span className="cinema-label text-cinema-black block">This Month</span>
                      <span className="font-display text-cinema-black font-bold text-base lg:text-lg block mt-1">Last Friday</span>
                      <span className="font-display text-cinema-black text-xs lg:text-sm block">5:00 PM</span>
                    </div>

                    {/* Speaker image */}
                    <div className="cinema-image-wrap aspect-[3/4] lg:aspect-[3/4] lg:max-h-[580px] overflow-hidden">
                      {(() => {
                        const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
                        return hasPhoto ? (
                          <SpeakerImage
                            speaker={speaker}
                            loading="eager"
                            fit="hero"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-cinema-charcoal flex flex-col items-center justify-center gap-4 lg:gap-6">
                            <Mic className="w-12 h-12 lg:w-16 lg:h-16 text-cinema-gold/20" />
                            <div className="text-center px-6 lg:px-8">
                              <span className="cinema-label block">Coming Soon</span>
                              <p className="text-cinema-muted font-sans text-xs lg:text-sm mt-2">Speaker to be announced</p>
                            </div>
                          </div>
                        )
                      })()}
                      <div className="absolute inset-0 bg-gradient-to-t from-cinema-black/95 via-cinema-black/40 to-transparent" />

                      {/* Speaker info always visible when available */}
                      <div className="absolute bottom-0 inset-x-0 p-5 lg:p-8">
                        <span className="cinema-label block mb-2 lg:mb-3">This Month's Speaker</span>
                        {speaker?.name ? (
                          <>
                            <p className="font-display text-xl lg:text-2xl text-white font-light">{speaker.name}</p>
                            <p className="text-cinema-muted font-sans text-xs lg:text-sm mt-1">
                              {speaker.title}{speaker.company && ` · ${speaker.company}`}
                            </p>
                            {speaker.experience && (
                              <div className="mt-3 inline-flex items-center gap-2 text-cinema-gold">
                                <Award className="w-3.5 h-3.5" />
                                <span className="cinema-label text-xs font-semibold">{speaker.experience}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-cinema-muted font-sans text-sm">To be announced</p>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </section>

          {/* ── QUICK NAVIGATION HUB ───────────────────────────────────────── */}
          <MCSection id="hub">
            <Reveal>
              <span className="cinema-label">Explore the Ecosystem</span>
              <h2 className="cinema-headline-lg mt-4 sm:mt-8">
                Everything in one <span className="text-cinema-gold">place</span>
              </h2>
              <div className="cinema-divider mt-8 sm:mt-10 mb-10 sm:mb-16" />
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {QUICK_NAV.map(({ icon: Icon, title, desc, to, cta }, i) => (
                <Reveal key={to} delay={i * 60}>
                  <Link
                    to={to}
                    className={`group block h-full p-6 sm:p-8 transition-colors duration-500 ${
                      cta ? 'text-cinema-glow' : 'hover:text-cinema-gold'
                    }`}
                  >
                    <div className={`inline-flex mb-6 transition-colors ${
                      cta
                        ? 'text-cinema-glow'
                        : 'text-cinema-muted group-hover:text-cinema-glow'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className={`font-display text-lg ${cta ? 'text-cinema-glow' : 'text-white group-hover:text-cinema-gold'} transition-colors`}>
                      {title}
                    </p>
                    <p className="text-cinema-muted font-sans text-sm mt-2 leading-relaxed">{desc}</p>
                    <div className={`mt-6 flex items-center gap-2 text-sm font-sans transition-colors ${cta ? 'text-cinema-glow' : 'text-cinema-muted group-hover:text-cinema-gold'}`}>
                      <span>{cta ? 'Register Now' : 'Explore'}</span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </MCSection>

          {/* ── FEATURED SPEAKER ─────────────────────────────────────────── */}
          {speaker?.name && (
            <MCSection id="speaker" bg="charcoal">
              <Reveal>
                <span className="cinema-label">Featured Speaker</span>
                <div className="cinema-divider mt-6 mb-10 sm:mb-16" />
              </Reveal>
              <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-32 items-start">
                <Reveal variant="scale">
                  <div className="cinema-image-wrap aspect-[3/4] max-w-sm">
                    {(() => {
                      const hasPhoto = resolveImageUrl(speaker?.photo_url || speaker?.photo)
                      return hasPhoto ? (
                        <SpeakerImage speaker={speaker} fit="hero" />
                      ) : (
                        <div className="w-full h-full bg-cinema-graphite flex items-center justify-center">
                          <Mic className="w-20 h-20 text-cinema-muted/20" />
                        </div>
                      )
                    })()}
                    <div className="absolute inset-0 bg-gradient-to-t from-cinema-black/60 via-transparent to-transparent" />
                  </div>
                </Reveal>

                <Reveal delay={150}>
                  <div className="space-y-6 sm:space-y-8">
                    <div>
                      <h2 className="cinema-headline-lg">{speaker.name}</h2>
                      <p className="mt-3 text-cinema-gold font-sans text-lg font-light">{speaker.title}</p>
                      {speaker.company && <p className="cinema-body text-sm mt-1">{speaker.company}</p>}
                    </div>

                    {speaker.experience && (
                      <div className="flex items-center gap-4 text-cinema-gold">
                        <Award className="w-5 h-5 shrink-0" />
                        <span className="text-white font-sans text-sm">{speaker.experience}</span>
                      </div>
                    )}

                    {speaker.bio && (
                      <p className="cinema-body text-sm leading-relaxed max-w-lg">{speaker.bio}</p>
                    )}

                    {speaker.title && (
                      <div className="flex flex-wrap gap-2">
                        {speaker.title.split(/[,·\n]/).filter(Boolean).map((tag, i) => (
                          <span key={i} className="text-xs text-cinema-muted font-sans uppercase tracking-wider">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      {[
                        [speaker.linkedin, Linkedin, 'LinkedIn'],
                        [speaker.instagram, Instagram, 'Instagram'],
                        [speaker.twitter, Twitter, 'Twitter'],
                        [speaker.facebook, Facebook, 'Facebook'],
                      ].filter(([url]) => url).map(([url, Icon, label]) => (
                        <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                          className="p-3 text-cinema-muted hover:text-cinema-gold transition-colors duration-300"
                          aria-label={label}>
                          <Icon className="w-4 h-4" />
                        </a>
                      ))}
                    </div>
                  </div>
                </Reveal>
              </div>
            </MCSection>
          )}

          {/* ── SESSION DETAILS ───────────────────────────────────────────── */}
          <MCSection id="session">
            <Reveal>
              <span className="cinema-label">Session Details</span>
              <div className="cinema-divider mt-6 mb-10 sm:mb-16" />
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { Icon: Mic, label: 'Topic', value: mc?.topic || 'Session details coming soon' },
                { Icon: Calendar, label: 'Date', value: formatDate(mc?.event_date) },
                { Icon: Clock, label: 'Time', value: formatTime(mc?.start_time) },
                { Icon: MapPin, label: 'Venue', value: mc?.venue || 'Synergy Multimedia Academy, Kampala' },
                { Icon: Award, label: 'Duration', value: mc?.duration_minutes ? `${mc.duration_minutes} minutes` : '~2 Hours' },
                { Icon: Users, label: 'Access & Fee', value: 'FREE for SMA Students · UGX 150,000 for Non-SMA Guests' },
              ].map(({ Icon, label, value }, i) => (
                <Reveal key={label} delay={i * 60}>
                  <div className="p-5 sm:p-8 group">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="w-5 h-5 text-cinema-gold" />
                      <span className="cinema-label">{label}</span>
                    </div>
                    <p className="text-white font-sans text-sm leading-relaxed">{value}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {mc?.requirements && (
              <Reveal delay={200}>
                <div className="p-5 sm:p-8 border-t border-white/5">
                  <span className="cinema-label block mb-3">Requirements</span>
                  <p className="cinema-body text-sm">{mc.requirements}</p>
                </div>
              </Reveal>
            )}
          </MCSection>

          {/* ── WHAT YOU'LL LEARN ─────────────────────────────────────────── */}
          <MCSection id="learn" bg="charcoal">
            <Reveal>
              <span className="cinema-label">What You'll Learn</span>
              <h2 className="cinema-headline-lg mt-4 sm:mt-8">
                Skills &amp; <span className="text-cinema-gold">Takeaways</span>
              </h2>
              <div className="cinema-divider mt-8 sm:mt-10 mb-10 sm:mb-16" />
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whatYouLearn.map((item, i) => (
                <Reveal key={i} delay={i * 50}>
                  <div className="p-4 sm:p-6 flex items-center gap-4 transition-colors duration-500 group">
                    <span className="text-xl shrink-0 group-hover:scale-110 transition-transform duration-300">
                      {typeof item === 'object' ? item.icon : '✓'}
                    </span>
                    <span className="cinema-body text-sm group-hover:text-white transition-colors">
                      {typeof item === 'object' ? item.text : item}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </MCSection>

          {/* ── REGISTRATION ─────────────────────────────────────────────── */}
          <MCSection id="register" ref={formRef}>
            <div className="relative">
              <div className="cinema-glow-orb w-[600px] h-[400px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
              <div className="relative max-w-[1280px] mx-auto">
                <Reveal>
                  <span className="cinema-label">Registration</span>
                  <h2 className="cinema-headline-lg mt-4 sm:mt-8">
                    Register for<br className="hidden sm:block" />
                    <span className="text-cinema-gold">This Month's Session</span>
                  </h2>
                  <p className="cinema-body mt-4 sm:mt-6 max-w-lg">
                    <strong className="text-white">UGX 150,000</strong> for non-SMA guests · <strong className="text-cinema-glow">FREE</strong> for current SMA students. Limited seats. Come ready to learn, network and grow.
                  </p>
                  <div className="cinema-divider mt-8 sm:mt-10 mb-8 sm:mb-12" />
                </Reveal>
                <Reveal delay={150}>
                  {mc ? (
                    <MasterclassRegistrationForm masterclass={mc} onSuccess={handleSuccess} />
                  ) : (
                    <div className="text-center py-16">
                      <div className="text-5xl mb-6">📅</div>
                      <h3 className="font-display text-white text-2xl font-light mb-3">No Upcoming Session Scheduled</h3>
                      <p className="cinema-body text-sm">Check back soon. The next masterclass will be announced shortly.</p>
                    </div>
                  )}
                </Reveal>
              </div>
            </div>
          </MCSection>

          {/* ── PAST SESSIONS ─────────────────────────────────────────────── */}
          {pastSessions.length > 0 && (
            <MCSection id="past-sessions" bg="charcoal">
              <Reveal>
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                  <div>
                    <span className="cinema-label">Past Sessions</span>
                    <h2 className="cinema-headline-lg mt-4 sm:mt-6">
                      Previous <span className="text-cinema-gold">Masterclasses</span>
                    </h2>
                  </div>
                  <Link to="/masterclass/speakers" className="cinema-btn-text group/link self-start sm:self-auto">
                    All Speakers
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
                <div className="cinema-divider mt-8 sm:mt-10 mb-10 sm:mb-16" />
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {pastSessions.map((session, i) => (
                  <Reveal key={session.id} delay={i * 80}>
                    <PastSessionCard session={session} />
                  </Reveal>
                ))}
              </div>
            </MCSection>
          )}

          {/* ── BOTTOM CTA ────────────────────────────────────────────────── */}
          <MCSection id="cta">
            <div className="relative max-w-4xl mx-auto text-center">
              <div className="cinema-glow-orb w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20" />
              <Reveal>
                <span className="cinema-label">Ready for the next session?</span>
                <h2 className="cinema-headline-xl mt-6">
                  Join the next <span className="text-cinema-gold">Masterclass</span>
                </h2>
                <p className="cinema-body-lg mt-6 max-w-2xl mx-auto text-cinema-muted">
                  FREE for current SMA students · UGX 150,000 for non-SMA guests. Limited seats. Come ready to learn, network, and grow with Kampala's leading creatives.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button onClick={scrollToForm} className="cinema-btn-primary group">
                    Register for the Next Session
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <Link to="/masterclass/archive" className="cinema-btn-ghost">
                    Browse Archive
                  </Link>
                </div>
              </Reveal>
            </div>
          </MCSection>
        </>
      )}

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />

      {/* Sticky mobile register button */}
      {mc && !registered && (
        <div className="fixed bottom-0 inset-x-0 z-40 px-4 pb-4 pt-8 sm:hidden"
          style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.98) 60%, transparent)' }}>
          <button onClick={scrollToForm} className="cinema-btn-primary w-full group">
            Register for Masterclass
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      )}

      {/* Schema.org Event JSON-LD */}
      {mc && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: `${SCHEDULE_SERIES_NAME} – ${mc.topic}`,
          description: mc.description || `${SCHEDULE_SERIES_NAME}: ${mc.topic}. Where Industry Meets Education.`,
          startDate: mc.event_date ? `${mc.event_date}T${mc.start_time || '17:00'}:00` : undefined,
          endDate: mc.event_date && mc.duration_minutes ? (() => {
            const start = new Date(`${mc.event_date}T${mc.start_time || '17:00'}:00`)
            if (isNaN(start.getTime())) return undefined
            return new Date(start.getTime() + mc.duration_minutes * 60000).toISOString()
          })() : undefined,
          location: { '@type': 'Place', name: mc.venue || 'Synergy Multimedia Academy', address: { '@type': 'PostalAddress', addressLocality: 'Kampala', addressCountry: 'UG' } },
          organizer: { '@type': 'Organization', name: 'Synergy Multimedia Academy', url: 'https://synergymultimediaacademy.com' },
          performer: mc.speaker_name ? { '@type': 'Person', name: mc.speaker_name, jobTitle: mc.speaker_title } : undefined,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          offers: {
            '@type': 'Offer',
            price: '150000',
            priceCurrency: 'UGX',
            availability: mc.registration_open ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            url: 'https://synergymultimediaacademy.com/masterclass',
            description: 'UGX 150,000 for non-SMA guests. FREE for current SMA students.'
          },
          image: resolveImageUrl(mc.speaker_photo) || 'https://synergymultimediaacademy.com/Logos/SMA LOGO  DARK MODE.png',
          url: 'https://synergymultimediaacademy.com/masterclass',
        }) }} />
      )}
    </div>
  )
}

// ── sub-components ─────────────────────────────────────────────────────────

const MCSection = forwardRef(function MCSection({ id, children, bg }, ref) {
  return (
    <section id={id} ref={ref}
      className={`relative py-16 sm:py-24 lg:py-40 overflow-hidden ${
        bg === 'charcoal' ? 'bg-cinema-charcoal' : 'bg-cinema-black'
      }`}>
      <div className="cinema-container relative">
        {children}
      </div>
    </section>
  )
})

function PastSessionCard({ session }) {
  return (
    <Link to={`/masterclass/${session.slug || session.id}`} className="cinema-course-card group flex flex-col h-full">
      {session.speaker_photo && (
        <div className="cinema-image-wrap aspect-[4/5] max-h-52 overflow-hidden">
          <SpeakerImage speaker={{ photo_url: session.speaker_photo, name: session.speaker_name }} alt={session.speaker_name} loading="lazy" fit="card" />
          <div className="cinema-course-overlay" />
        </div>
      )}
      <div className="p-5 sm:p-8 flex flex-col flex-1">
        {session.speaker_name && (
          <span className="cinema-label block mb-4">
            {session.speaker_name}{session.speaker_title ? ` · ${session.speaker_title}` : ''}
          </span>
        )}
        <h3 className="font-display text-xl text-white font-light leading-tight flex-1 group-hover:text-cinema-gold transition-colors">{session.topic}</h3>
        <div className="flex items-center gap-2 text-cinema-muted font-sans text-xs mt-6">
          <Calendar className="w-3.5 h-3.5 text-cinema-gold" />
          {isValidEventDate(session.event_date) ? new Date(session.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' }) : 'TBA'}
        </div>
        <div className="cinema-btn-text mt-5 group/rec">
          <ArrowRight className="w-3.5 h-3.5" />
          View Recap
          <ArrowRight className="w-3 h-3 transition-transform group-hover/rec:translate-x-1" />
        </div>
      </div>
    </Link>
  )
}




